<template>
  <v-dialog v-model="dialogControl" max-width="800" scrollable>
    <v-card v-if="initiative">
      <v-card-title class="headline pb-0">
        <span>
          {{ `Aprovar #${initiative.id} - ${initiative.key}` }}
        </span>
      </v-card-title>

      <v-divider class="ma-0"></v-divider>

      <v-card-text class="pt-0 content-scroll">
        <v-data-table :headers="headers" :items="tableSections" hide-actions>
          <template slot="items" slot-scope="props">
            <td class="text-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-checkbox
                    v-model="checks[props.item.key]"
                    v-bind="attrs"
                    v-on="on"
                    :disabled="!props.item.exists"
                    hide-details
                    color="success"
                    class="mt-0 pt-0"
                  ></v-checkbox>
                </template>
                <span> Aprovar {{ props.item.name }} </span>
              </v-tooltip>
            </td>

            <td>
              <div class="py-3">
                <h3 class="font-weight-medium mb-2">
                  {{ props.item.name }}
                </h3>

                <!-- Header - Cabeçalho -->
                <div v-if="props.item.key === 'header'">
                  <div v-if="header.format === 'IMAGE'">
                    <a
                      :href="header.example.header_handle[0]"
                      target="_blank"
                      rel="noopener noreferrer cursor-pointer"
                      class="no-decoration"
                    >
                      <v-img
                        :src="header.example.header_handle[0]"
                        max-width="150"
                        max-height="150"
                        contain
                        class="grey lighten-3 rounded"
                      ></v-img>
                    </a>
                  </div>

                  <div v-else-if="header.format === 'VIDEO'">
                    <video
                      :src="header.example.header_handle[0]"
                      controls
                      class="media-preview"
                    ></video>
                  </div>

                  <div v-else-if="header.format === 'DOCUMENT'">
                    <a
                      :href="header.example.header_handle[0]"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="d-flex align-center text-decoration-none"
                    >
                      <v-icon left color="primary">
                        mdi-file-document-outline
                      </v-icon>
                      <span>Abrir documento</span>
                    </a>
                  </div>

                  <div v-else>
                    {{ header.example.header_handle[0] }}
                  </div>
                </div>

                <!-- Body - Corpo da Mensagem -->
                <div v-else-if="props.item.key === 'message'">
                  <div class="message-container">
                    <div
                      class="green lighten-5 pa-3 elevation-1 message-bubble"
                    >
                      {{ body.text.trim() }}

                      <div class="timestamp-wrapper">
                        <span class="mr-1 align-middle">10:30 AM</span>
                        <v-icon
                          small
                          color="blue lighten-2"
                          class="align-middle"
                        >
                          mdi-check-all
                        </v-icon>
                      </div>
                    </div>
                  </div>
                  <span v-if="initiative.vars && initiative.vars.length">
                    Variaveis: {{ initiative.vars.join(", ") }}
                  </span>
                </div>

                <!-- Footer - Rodapé -->
                <div v-else-if="props.item.key === 'footer'">
                  <span class="grey--text text--darken-2">
                    {{ footer.text }}
                  </span>
                </div>

                <!-- Buttons - Botões -->
                <div v-else-if="props.item.key === 'buttons'">
                  <MetaButtonsDisplay :buttons="buttons" />
                </div>
              </div>
            </td>
          </template>
        </v-data-table>
      </v-card-text>

      <v-divider class="ma-0"></v-divider>

      <v-card-actions>
        <v-btn
          color="blue lighten-1"
          text
          outline
          dark
          @click="openPreviewDialog()"
        >
          Visualizar
        </v-btn>

        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          outline
          dark
          @click="closeDialog"
          :disabled="loading"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="accent"
          depressed
          :loading="loading"
          @click="saveInitiative"
          :disabled="!canApprove || loading"
        >
          Aprovar
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card v-else>
      <v-card-text class="pa-4">Sem dados disponíveis.</v-card-text>
    </v-card>

    <InitiativePreview
      ref="InitiativePreview"
      :previewDialog.sync="previewDialog"
    />
  </v-dialog>
</template>

<script>
import { mapMutations } from "vuex";
import InitiativePreview from "./Preview/Preview";
import MetaButtonsDisplay from "./MetaButtonsDisplay.vue";

export default {
  name: "ApproveDialog",
  components: {
    InitiativePreview,
    MetaButtonsDisplay,
  },
  props: {
    value: { type: Boolean, required: true },
    loading: { type: Boolean, default: false },
    initiative: { type: Object | null, required: true, default: () => ({}) },
  },
  emits: ["input", "close", "approve", "disapprove"],
  data() {
    return {
      previewDialog: false,
      headers: [
        {
          text: "Aprovar",
          value: "check",
          sortable: false,
          width: "80px",
          align: "center",
        },
        { text: "Detalhes da Seção", value: "content", sortable: false },
      ],
      checks: {
        header: false,
        message: false,
        footer: false,
        buttons: false,
      },
      availableComponents: [],
      tableSections: [],
      // componentes disponíveis para aprovar:
      buttons: null,
      header: null,
      body: null,
      footer: null,
    };
  },
  computed: {
    dialogControl: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    canApprove() {
      if (
        !this.initiative ||
        !this.initiative.meta_structure ||
        !this.availableComponents.length
      )
        return false;

      const requiredChecks = this.tableSections.filter(
        (s) => s.requiredForApproval
      );

      return requiredChecks.every((section) => this.checks[section.key]);
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.getInitiativeComponents();
        if (this.initiative.approved) {
          this.checks.header = true;
          this.checks.message = true;
          this.checks.footer = true;
          this.checks.buttons = true;
        } else {
          this.checks.header = false;
          this.checks.message = false;
          this.checks.footer = false;
          this.checks.buttons = false;
        }
      } else {
        this.availableComponents = [];
        this.tableSections = [];
      }
    },
  },
  methods: {
    ...mapMutations("initiativeManager", {
      setSelectedInitiative: "setSelectedInitiative",
      clearSelectedInitiative: "clearSelectedInitiative",
    }),

    getInitiativeComponents() {
      if (!this.initiative || !this.initiative.meta_structure) return;

      const components = this.initiative.meta_structure.components || [];

      if (!components.length) return;

      this.availableComponents = components.map((c) => c.type);

      if (this.availableComponents.includes("HEADER")) {
        this.header =
          this.initiative.meta_structure.components.find(
            (c) => c.type === "HEADER"
          ) || null;

        this.tableSections.push({
          key: "header",
          name: "Cabeçalho",
          exists: true,
          requiredForApproval: true,
        });
      }

      if (this.availableComponents.includes("BODY")) {
        this.body =
          this.initiative.meta_structure.components.find(
            (c) => c.type === "BODY"
          ) || null;

        this.tableSections.push({
          key: "message",
          name: "Corpo da Mensagem",
          exists: true,
          requiredForApproval: true,
        });
      }

      if (this.availableComponents.includes("FOOTER")) {
        this.footer =
          this.initiative.meta_structure.components.find(
            (c) => c.type === "FOOTER"
          ) || null;

        this.tableSections.push({
          key: "footer",
          name: "Footer",
          exists: true,
          requiredForApproval: true,
        });
      }

      if (this.availableComponents.includes("BUTTONS")) {
        this.buttons =
          this.initiative.meta_structure.components.find(
            (c) => c.type === "BUTTONS"
          ) || null;

        this.tableSections.push({
          key: "buttons",
          name: "Botões",
          exists: true,
          requiredForApproval: true,
        });
      }
    },

    closeDialog() {
      this.dialogControl = false;
      this.$emit("close");
      this.clearSelectedInitiative();
    },
    saveInitiative() {
      if (this.canApprove && !this.initiative.approved) {
        this.$emit("approve", this.initiative);
      } else if (!this.canApprove && this.initiative.approved) {
        this.$emit("disapprove", this.initiative);
      }
      this.closeDialog();
      this.clearSelectedInitiative();
    },
    getStatusColor(status) {
      if (status === "APPROVED") return "success";
      if (status === "REJECTED") return "error";
      return "grey";
    },
    openPreviewDialog() {
      this.setSelectedInitiative(this.initiative);
      this.previewDialog = true;
    },
  },
};
</script>

<style scoped>
.text-center {
  text-align: center;
}

.content-scroll {
  max-height: 70vh;
}

.no-decoration {
  text-decoration: none;
}

.media-preview {
  max-width: 150px;
  max-height: 150px;
  border-radius: 4px;
}

.message-container {
  max-width: 85%;
}

.message-bubble {
  border-radius: 8px 8px 8px 2px;
}

.timestamp-wrapper {
  font-size: 10px;
  color: #999;
  text-align: right;
  margin-top: 4px;
  line-height: 1;
}

.align-middle {
  vertical-align: middle;
}
</style>